import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TelemetryPeriod, telemetryPeriod } from '@core/constants/telemetry-route.constant';
import { ChartRangeOptions, InstallationTelemetry } from '@core/models';
import { InstallationService } from '@core/services/installation.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import moment from 'moment';
import { pipe, switchMap, tap } from 'rxjs';

type InstallationTelemetryState = {
  installationOid: string | null;
  range: ChartRangeOptions;
  selectedPeriod: TelemetryPeriod;
  responses: Partial<Record<ChartRangeOptions, InstallationTelemetry | null>>;
  // The period each response was requested for, so a reader can tell whether the response on
  // screen is still the previous period's while the selected one loads.
  responsePeriods: Partial<Record<ChartRangeOptions, TelemetryPeriod>>;
  isLoading: boolean;
  failed: boolean;
  // The installation is visible, but its telemetry is not shared with the user's organisation.
  notShared: boolean;
};

const initialState = (): InstallationTelemetryState => ({
  installationOid: null,
  range: ChartRangeOptions.Month,
  selectedPeriod: telemetryPeriod(ChartRangeOptions.Month, moment()),
  responses: {},
  responsePeriods: {},
  isLoading: false,
  failed: false,
  notShared: false,
});

// Provided by the installation detail page, so its cache is one installation's and a second
// installation starts from a clean slate. Every card and every explorer column reads the one
// response held here.
export const InstallationTelemetryStore = signalStore(
  withState(initialState),
  withComputed(store => ({
    telemetry: computed(() => store.responses()[store.range()] ?? null),
    telemetryPeriod: computed(() => store.responsePeriods()[store.range()] ?? null),
  })),
  withMethods((store, installationService = inject(InstallationService)) => ({
    setInstallation: (installationOid: string) => patchState(store, { installationOid }),

    // Only the range moves here: the date control re-emits its reference date for the new
    // range, and that emission is the one fetch.
    setRange: (range: ChartRangeOptions) => patchState(store, { range }),

    setPeriod: (selectedPeriod: TelemetryPeriod) => patchState(store, { selectedPeriod }),

    fetch: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isLoading: true, failed: false })),
        switchMap(() => {
          const range = store.range();
          const period = store.selectedPeriod();
          return installationService.telemetry(store.installationOid() ?? '', period).pipe(
            tapResponse({
              next: (telemetry: InstallationTelemetry) =>
                patchState(store, {
                  responses: { ...store.responses(), [range]: telemetry },
                  responsePeriods: { ...store.responsePeriods(), [range]: period },
                  isLoading: false,
                  notShared: false,
                }),
              error: (error: unknown) =>
                patchState(store, {
                  responses: { ...store.responses(), [range]: null },
                  responsePeriods: { ...store.responsePeriods(), [range]: period },
                  isLoading: false,
                  failed: true,
                  notShared: error instanceof HttpErrorResponse && error.status === 404,
                }),
            }),
          );
        }),
      ),
    ),
  })),
);
